import React, { useState, useEffect, useCallback } from 'react';
import { salesService } from '../../services/sales';
import TopMenu from '../../contexts/TopMenu';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import '../../styles/Dashboard.css';

// Configure dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

function Dashboard({ email, onLogout }) {

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [allAvailableBrands, setAllAvailableBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day').startOf('day'));
  const [endDate, setEndDate] = useState(dayjs().endOf('day'));
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  const theme = createTheme({
    palette: {
      primary: {
        main: '#000000',
      },
    },
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& fieldset': {
              borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
              borderColor: '#000000',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#000000',
            },
          },
        },
      },
    },
  });

  const fetchSalesData = useCallback(async () => {
    setSalesLoading(true);
    setError('');
    try {
      const response = await salesService.getSalesData(
        email,
        localStorage.getItem('authKey'),
        localStorage.getItem('userId'),
        selectedBrands,
        startDate,
        endDate
      );

      if (response.status === 200) {
        setSalesData(response.data);
      } else {
        setError(response.message || 'Failed to fetch sales data');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch sales data');
      setSalesData(null);
    } finally {
      setSalesLoading(false);
    }
  }, [email, selectedBrands, startDate, endDate]);

  const handleDateChange = useCallback((type, newValue) => {
    if (!newValue || !newValue.isValid()) return;

    if (type === 'start') {
      setStartDate(newValue.startOf('day'));
    } else {
      setEndDate(newValue.endOf('day'));
    }
  }, []);

  useEffect(() => {
    fetchSalesData();
  }, [fetchSalesData]);

  useEffect(() => {
    if (salesData) {
      setLoading(false);
      if (salesData.all_brands && salesData.all_brands.length > 0) {
        setAllAvailableBrands(salesData.all_brands);
      } else if (salesData.brands && allAvailableBrands.length === 0) {
        setAllAvailableBrands(salesData.brands);
      }
    }
  }, [salesData, allAvailableBrands.length]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loader"></div>
        <p>Loading your brands...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>{error}</p>
        <button onClick={fetchSalesData}>Retry</button>
      </div>
    );
  }

  // Prepare chart data
  const salesTrends = salesData?.brands?.reduce((acc, brand) => {
    (brand.daily_data || []).forEach(day => {
      const existingDay = acc.find(d => d.date === day.date);
      if (existingDay) {
        existingDay.amount += (day.amount || 0);
        existingDay.units += (day.units || 0);
      } else {
        acc.push({ ...day });
      }
    });
    return acc;
  }, []).sort((a, b) => (a.date || '').localeCompare(b.date || '')) || [];

  const baseBrands = (allAvailableBrands && allAvailableBrands.length > 0)
    ? allAvailableBrands
    : (salesData?.all_brands || salesData?.brands || []);

  const brandMetrics = (salesData?.brands || []).map(col => {
    const colTitle = col.title || col.brand || 'Unknown';
    return {
      brand: colTitle,
      sales: col.total_sales || 0,
      units: col.total_units || 0
    };
  });

  const summary = salesData?.summary || {
    total_sales: 0,
    total_orders: 0,
    total_units: 0
  };

  return (
    <div className="dashboard-root">
      <TopMenu pageTitle="Sales Dashboard" email={email} onLogout={onLogout} />
      <div className="dashboard-container">
        <div className="dashboard-controls">
          <div className="brand-selector">
            <div className="brands-grid">
              {baseBrands.map((collection, idx) => {
                const brandName = collection.title || collection.brand || collection.name || collection.handle || 'Brand';
                const brandKey = collection.handle || collection.brand || collection.title || `brand-${idx}`;
                const initial = (brandName.trim().charAt(0) || 'B').toUpperCase();
                const isSelected = selectedBrands.includes(brandKey) || 
                  (collection.handle && selectedBrands.includes(collection.handle)) || 
                  (collection.brand && selectedBrands.includes(collection.brand)) ||
                  (collection.title && selectedBrands.includes(collection.title));
                const hasValidImage = collection.image_url && !collection.image_url.includes('placeholder-collection') && !imageErrors[brandKey];

                return (
                  <div 
                    key={brandKey}
                    className={`brand-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => {
                      const keyToToggle = collection.handle || collection.brand || collection.title || brandName;
                      setSelectedBrands(prev => 
                        prev.includes(keyToToggle) 
                          ? prev.filter(h => h !== keyToToggle)
                          : [...prev, keyToToggle]
                      );
                    }}
                    title={collection.description || brandName}
                  >
                    <div className="brand-image">
                      {hasValidImage ? (
                        <img 
                          src={collection.image_url} 
                          alt={brandName}
                          onError={() => {
                            setImageErrors(prev => ({ ...prev, [brandKey]: true }));
                          }}
                        />
                      ) : (
                        <div className="brand-placeholder-avatar">
                          {initial}
                        </div>
                      )}
                    </div>
                    <div className="brand-name">{brandName}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="sales-data">
          <div className="date-range-picker">
            <ThemeProvider theme={theme}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  alignItems: 'center',
                  marginBottom: 3,
                  '& .MuiInputBase-root': {
                    height: '42px',
                    fontSize: '14px',
                  }
                }}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => handleDateChange('start', newValue)}
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                    }}
                  />
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => handleDateChange('end', newValue)}
                    minDate={startDate}
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                    }}
                  />
                </Box>
              </LocalizationProvider>
            </ThemeProvider>
          </div>

          <div className="sales-summary">
            <div className="summary-card total-sales">
              <h3>Total Sales</h3>
              <p className="amount">₹{(summary?.total_sales || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="summary-card total-orders">
              <h3>Total Orders</h3>
              <p className="amount">{(summary?.total_orders || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="summary-card total-units">
              <h3>Total Units</h3>
              <p className="amount">{(summary?.total_units || 0).toLocaleString('en-IN')}</p>
            </div>
          </div>

          <div className="charts-container">
            {salesLoading ? (
              <div className="charts-loading">
                <div className="loader"></div>
                <p>Loading sales data...</p>
              </div>
            ) : (
              <>
                <div className="chart-card sales-trend">
                  <h3>Sales Trend</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={salesTrends} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis 
                        dataKey="date" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#999', fontSize: 12 }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#999', fontSize: 12 }}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: 'none',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          borderRadius: '4px',
                          padding: '12px'
                        }}
                        labelStyle={{ color: '#333', fontWeight: 300, marginBottom: '8px' }}
                        itemStyle={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="amount" 
                        stroke="#000000" 
                        strokeWidth={1.5}
                        dot={false}
                        activeDot={{ r: 4, fill: '#000000' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="chart-card brand-performance">
                  <h3>Brand Performance</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={brandMetrics} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                      <XAxis 
                        dataKey="brand" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#999', fontSize: 12 }}
                      />
                      <YAxis 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#999', fontSize: 12 }}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: 'none',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          borderRadius: '4px',
                          padding: '12px'
                        }}
                        labelStyle={{ color: '#333', fontWeight: 300, marginBottom: '8px' }}
                        itemStyle={{ color: '#666', fontSize: '12px', textTransform: 'uppercase' }}
                      />
                      <Bar 
                        dataKey="sales" 
                        fill="#000000" 
                        radius={[2, 2, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard; 